/* Add an ambulance to your own fleet (owner, 2026-10-30: "I can onboard an
 * ambulance"). Until then an agency's ambulance came only with a driver who
 * owned it. POST /agency/vehicles (OWNER / ADMIN): the plate, make, model,
 * year and what it carries — no kind: an ambulance has no type (owner,
 * 2026-10-30), any ambulance is sent to any ambulance booking. It is on Team at once as "Your fleet" and
 * takes trips once its papers (RC, insurance, permit, PUC, fitness) are
 * uploaded on Documents and approved — the dispatch gate reads them, as for
 * every vehicle. A plate already on record is refused. */

import { useState } from 'react';
import { addAmbulance } from '../data/agency';
import { errorText } from '../data/words';
import { Btn, Field, Modal, Note, uiCss as css } from '../ui';

const EQUIPMENT = [
  { key: 'has_oxygen', label: 'Oxygen' },
  { key: 'has_ventilator', label: 'Ventilator' },
  { key: 'has_defibrillator', label: 'Defibrillator' },
  { key: 'has_suction', label: 'Suction' },
] as const;
type Kit = Record<(typeof EQUIPMENT)[number]['key'], boolean>;
const NO_KIT: Kit = { has_oxygen: false, has_ventilator: false, has_defibrillator: false, has_suction: false };

export function AddAmbulance({ open, onClose, onAdded }: { open: boolean; onClose: () => void; onAdded: (plate: string) => void }) {
  const [plate, setPlate] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  const [kit, setKit] = useState<Kit>(NO_KIT);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setPlate('');
    setMake('');
    setModel('');
    setYear('');
    setKit(NO_KIT);
    setError(null);
  };
  const close = () => {
    reset();
    onClose();
  };

  const thisYear = new Date().getFullYear();
  const y = Number(year);
  const blocked = !plate.trim()
    ? 'Type the registration number'
    : plate.trim().length > 24
      ? 'The registration number is too long'
      : !make.trim() || !model.trim()
        ? 'Type the make and the model'
        : !/^\d{4}$/.test(year.trim()) || y < 1990 || y > thisYear + 1
          ? 'Type the year it was made (four digits)'
          : null;

  const save = async () => {
    if (blocked) return;
    setBusy(true);
    setError(null);
    try {
      await addAmbulance({
        registration_number: plate.trim().toUpperCase(),
        make: make.trim(),
        model: model.trim(),
        manufacture_year: y,
        ...kit,
      });
      const p = plate.trim().toUpperCase();
      reset();
      onAdded(p);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const input = (value: string, set: (v: string) => void, label: string, placeholder?: string) => (
    <input className={css.input} value={value} onChange={(e) => set(e.target.value)} placeholder={placeholder} aria-label={label} />
  );

  return (
    <Modal open={open} onClose={close} label="Add an ambulance">
      <div className={css.modalHead}>
        <div className={css.label}>Your fleet</div>
        <div className={css.modalTitle}>Add an ambulance</div>
        <div className={css.modalDesc}>
          An ambulance your agency owns, driven by whichever of your drivers you send. It takes trips once its papers are approved.
        </div>
      </div>
      <div className={css.modalBody}>
        <Field label="Registration number">{input(plate, setPlate, 'Registration number', 'RJ19 PA 1234')}</Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 0.7fr', gap: 10 }}>
          <Field label="Make">{input(make, setMake, 'Make', 'Force')}</Field>
          <Field label="Model">{input(model, setModel, 'Model', 'Traveller')}</Field>
          <Field label="Year">{input(year, setYear, 'Year made', String(thisYear))}</Field>
        </div>
        <div>
          <div className={css.label} style={{ marginBottom: 7 }}>
            What it carries
          </div>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            {EQUIPMENT.map((e) => (
              <label key={e.key} style={{ display: 'flex', gap: 6, alignItems: 'center', font: '600 13px var(--font-ui)' }}>
                <input type="checkbox" checked={kit[e.key]} onChange={(ev) => setKit((k) => ({ ...k, [e.key]: ev.target.checked }))} />
                {e.label}
              </label>
            ))}
          </div>
        </div>
        <Note tone="info">
          Next: on <strong>Documents</strong>, upload its RC, insurance, permit, PUC and fitness certificate. Families are sent it once they are approved.
        </Note>
        {error ? <Note tone="danger">{error}</Note> : null}
        {blocked && (plate || make || model || year) ? <span className={css.sub}>{blocked}.</span> : null}
      </div>
      <div className={css.modalFoot}>
        <Btn onClick={close}>Cancel</Btn>
        <Btn variant="primary" disabled={!!blocked || busy} onClick={() => void save()}>
          {busy ? 'Adding…' : 'Add ambulance'}
        </Btn>
      </div>
    </Modal>
  );
}
